import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Investment as InvestmentType,
  Partenrs,
} from "../../types/investment.type";
import { api } from "../../api/api";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import useGetRequest from "../../hooks/useGetRequest";
import { Cashbox } from "../../types/cashbox.type";
import dayjs from "dayjs";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import { Partner } from "../../types/partner.type";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export default function Investment() {
  const { itemId, name } = useParams();
  const { exchangeRate } = useExchangeRate();
  const [investments, setInvestments] = useState<InvestmentType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [form] = Form.useForm();
  const { data: cashboxesList, loading: cashboxesLoading } =
    useGetRequest("/cashbox/list");
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const fetchPartner = async () => {
    try {
      const response = await api.get(`/partners/${itemId}`);
      setPartner(response.data);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получения данных партнера.");
    }
  };

  const fetchInvestments = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/partners/${itemId}/investments`, {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
        },
      });
      setInvestments(response.data.investments);
      setTotal(response.data.total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение инвестиций.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartner();
    fetchInvestments();
  }, [page, pageSize, dateRange]);

  useEffect(() => {
    fetchPartner();
  }, [itemId]);

  useEffect(() => {

    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  const handleSubmit = async (values: any) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };

      await api.post(`/partners/${itemId}/investment`, data);
      message.success("Инвестиция успешно добавлена.");
    } catch (error) {
      console.error("Error saving investment:", error);
      message.error("Ошибка добавления инвестиции.");
    } finally {
      setConfirmLoading(false);
      setIsModalOpen(false);
      fetchInvestments();
      form.resetFields();
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await api.delete(`/partners/${id}/investment`);
      message.success("Инвестиция успешно удалена.");
      fetchInvestments();
    } catch (error: any) {
      console.error("Error deleting investment:", error);
      message.error(error.response.data.error);
    } finally {
      setDeletingId(null);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString('ru-RU');
    const periodText = dateRange 
      ? `${dateRange[0].format('DD.MM.YYYY')} - ${dateRange[1].format('DD.MM.YYYY')}`
      : 'Все время';

    // Подсчет итогов
    const totalsByCurrency = investments.reduce((acc, investment) => {
      if (!acc[investment.currency]) {
        acc[investment.currency] = 0;
      }
      acc[investment.currency] += investment.amount;
      return acc;
    }, {} as Record<string, number>);

    // Расчет общей суммы в USD с учетом обменного курса
    const totalInUSD = investments.reduce((acc, investment) => {
      if (investment.currency === 'USD') {
        return acc + investment.amount;
      } else if (investment.currency === 'TJS' && exchangeRate) {
        return acc + (investment.amount / exchangeRate);
      }
      return acc;
    }, 0);

    // Группировка по месяцам
    const monthlyTotals = investments.reduce((acc, investment) => {
      // Парсим дату в формате DD.MM.YYYY HH:MM или DD.MM.YY
      let date;
      try {
        if (investment.date.includes('.')) {
          // Отделяем дату от времени если есть
          const dateOnly = investment.date.split(' ')[0];
          const [day, month, year] = dateOnly.split('.');
          
          // Проверяем, что все части даты существуют
          if (!day || !month || !year) {
            console.warn('Invalid date format:', investment.date);
            return acc;
          }
          
          const fullYear = year.length === 2 ? `20${year}` : year;
          date = new Date(`${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        } else {
          date = new Date(investment.date);
        }
        
        // Проверяем валидность даты
        if (isNaN(date.getTime())) {
          console.warn('Invalid date:', investment.date);
          return acc;
        }
      } catch (error) {
        console.error('Date parsing error:', error, investment.date);
        return acc;
      }
      
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('ru-RU', { year: 'numeric', month: 'long' });
      
      if (!acc[monthKey]) {
        acc[monthKey] = {
          monthName,
          totalUSD: 0,
          investments: []
        };
      }
      
      // Добавляем сумму в USD
      if (investment.currency === 'USD') {
        acc[monthKey].totalUSD += investment.amount;
      } else if (investment.currency === 'TJS' && exchangeRate) {
        acc[monthKey].totalUSD += (investment.amount / exchangeRate);
      }
      
      acc[monthKey].investments.push(investment);
      return acc;
    }, {} as Record<string, { monthName: string; totalUSD: number; investments: any[] }>);

    const printHtml = `
      <html>
        <head>
          <title>Инвестиции ${name}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
              margin: 20px;
              color: #222;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .header h1 {
              margin: 0;
              font-size: 18px;
              font-weight: bold;
            }
            .header .info {
              margin-top: 5px;
              font-size: 12px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              border: 2px solid #000;
            }
            th, td {
              border: 1px solid #000;
              padding: 8px;
              text-align: left;
              font-size: 11px;
            }
            th {
              background-color: #f0f0f0;
              font-weight: bold;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .summary {
              margin-top: 20px;
              padding: 15px;
              border: 2px solid #1890ff;
              border-radius: 8px;
              background-color: #f0f8ff;
            }
            .summary h3 {
              margin: 0 0 10px 0;
              font-size: 14px;
              color: #1890ff;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
            }
            .summary-item {
              background: white;
              padding: 8px;
              border: 1px solid #d9d9d9;
              border-radius: 4px;
            }
            .summary-item strong {
              display: block;
              margin-bottom: 4px;
              color: #333;
            }
            @media print {
              body { margin: 10px; }
              .header { page-break-after: avoid; }
              .summary { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ИНВЕСТИЦИИ ${name?.toUpperCase() || ''}</h1>
            ${partner ? `
              <div style="margin: 15px 0; padding: 15px; background: #f0f8ff; border: 2px solid #1890ff; border-radius: 6px;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; font-size: 12px;">
                  <!-- Инвестиции -->
                  <div style="padding: 10px; background: #e6f3ff; border: 1px solid #91caff; border-radius: 4px;">
                    <div style="font-weight: bold; color: #1890ff; margin-bottom: 8px; font-size: 13px;">💰 Инвестиции</div>
                    <div style="margin-bottom: 5px;"><strong>Общ инвестиций:</strong> <strong style="color: #1890ff;">${partner.totalInvested.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} USD</strong></div>
                    <div style="margin-bottom: 5px;"><strong>Выведено:</strong> <strong style="color: #ff4d4f;">${partner.totalWithdrawnInvestment.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} USD</strong></div>
                    <div><strong>Баланс инвестиций:</strong> <strong style="color: #52c41a;">${partner.balanceInvestments.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} USD</strong></div>
                  </div>
                  <!-- Прибыль -->
                  <div style="padding: 10px; background: #f6ffed; border: 1px solid #95de64; border-radius: 4px;">
                    <div style="font-weight: bold; color: #52c41a; margin-bottom: 8px; font-size: 13px;">📈 Прибыль</div>
                    <div style="margin-bottom: 5px;"><strong>Общ прибыль:</strong> <strong style="color: #1890ff;">${(partner.balance + partner.totalWithdrawn).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} USD</strong></div>
                    <div style="margin-bottom: 5px;"><strong>Выведено прибыль:</strong> <strong style="color: #ff4d4f;">${partner.totalWithdrawn.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} USD</strong></div>
                    <div><strong>Баланс прибыль:</strong> <strong style="color: #52c41a;">${partner.balance.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} USD</strong></div>
                  </div>
                </div>
              </div>
            ` : ''}
            <div class="info">
              <div>Период: ${periodText}</div>
              <div>Дата печати: ${currentDate}</div>
              <div>Количество записей: ${investments.length}</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Дата</th>
                <th>Партнер</th>
                <th>Касса</th>
                <th>Валюта</th>
                <th>Сумма</th>
                <th>Описание</th>
                <th>Пользователь</th>
              </tr>
            </thead>
            <tbody>
              ${investments.map((investment, index) => `
                <tr>
                  <td style="text-align: center;">${index + 1}</td>
                  <td>${investment.date}</td>
                  <td>${investment.partnerName}</td>
                  <td>${investment.cashboxName || '-'}</td>
                  <td style="text-align: center;">${investment.currency}</td>
                  <td style="text-align: right; font-weight: bold;">${investment.amount.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                  <td>${investment.description || '-'}</td>
                  <td>${investment.username}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="summary">
            <h3>📊 Итоги по инвестициям</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <strong>Общие итоги по валютам:</strong>
                ${Object.entries(totalsByCurrency).map(([currency, amount]) => 
                  `<div>${currency}: ${amount.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</div>`
                ).join('')}
                <div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #ddd; font-weight: bold; color: #1890ff;">
                  Общая сумма в USD: ${totalInUSD.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div class="summary-item">
                <strong>Статистика:</strong>
                <div style="margin-bottom: 8px;">
                  <strong>Общее количество инвестиций:</strong> ${investments.length}
                </div>
                <div style="margin-bottom: 8px;">
                  <strong>Средняя инвестиция в USD:</strong> ${investments.length > 0 ? (totalInUSD / investments.length).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '0'}
                </div>
                <div>
                  <strong>Максимальная инвестиция в USD:</strong> ${investments.length > 0 ? Math.max(...investments.map(inv => {
                    if (inv.currency === 'USD') return inv.amount;
                    if (inv.currency === 'TJS' && exchangeRate) return inv.amount / exchangeRate;
                    return 0;
                  })).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '0'}
                </div>
              </div>
            </div>
          </div>

          <div class="summary">
            <h3>📅 Итоги по месяцам (USD)</h3>
            <div class="summary-grid">
              ${Object.entries(monthlyTotals).sort().map(([monthKey, monthData]) => 
                `<div class="summary-item">
                  <strong>${monthData.monthName}:</strong>
                  <div style="font-size: 16px; color: #1890ff; font-weight: bold; margin-top: 5px;">
                    ${monthData.totalUSD.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} USD
                  </div>
                  <div style="font-size: 11px; color: #666; margin-top: 3px;">
                    Количество инвестиций: ${monthData.investments.length}
                  </div>
                </div>`
              ).join('')}
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Партнер",
      dataIndex: "partnerName",
      key: "partnerName",
    },
    {
      title: "Касса",
      dataIndex: "cashboxName",
      key: "cashboxName",
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Пользователь",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Действия",
      dataIndex: "actions",
      key: "actions",
      render: (text: string, record: InvestmentType) => (
        <Space>
          <Button 
            type="link" 
            onClick={() => handleDelete(record._id)}
            loading={deletingId === record._id}
          >
            Удалить
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Инвестиции {name}</Title>
      
      {partner && (
        <div style={{ 
          marginBottom: 20, 
          padding: 20, 
          background: '#f5f5f5', 
          borderRadius: 8,
          border: '1px solid #d9d9d9'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30, fontSize: 15 }}>
            {/* Инвестиции */}
            <div style={{ 
              padding: 16, 
              background: '#e6f3ff', 
              borderRadius: 6,
              border: '1px solid #91caff'
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#1890ff', fontSize: 16 }}>💰 Инвестиции</h4>
              <div style={{ marginBottom: 8 }}>
                <strong>Общ инвестиций:</strong>{' '}
                <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                  {partner.totalInvested.toLocaleString('ru-RU', { 
                    minimumFractionDigits: 0, 
                    maximumFractionDigits: 2 
                  })} USD
                </span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>Выведено:</strong>{' '}
                <span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>
                  {partner.totalWithdrawnInvestment.toLocaleString('ru-RU', { 
                    minimumFractionDigits: 0, 
                    maximumFractionDigits: 2 
                  })} USD
                </span>
              </div>
              <div>
                <strong>Баланс инвестиций:</strong>{' '}
                <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                  {partner.balanceInvestments.toLocaleString('ru-RU', { 
                    minimumFractionDigits: 0, 
                    maximumFractionDigits: 2 
                  })} USD
                </span>
              </div>
            </div>

            {/* Прибыль */}
            <div style={{ 
              padding: 16, 
              background: '#f6ffed', 
              borderRadius: 6,
              border: '1px solid #95de64'
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#52c41a', fontSize: 16 }}>📈 Прибыль</h4>
              <div style={{ marginBottom: 8 }}>
                <strong>Общ прибыль:</strong>{' '}
                <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                  {(partner.balance + partner.totalWithdrawn).toLocaleString('ru-RU', { 
                    minimumFractionDigits: 0, 
                    maximumFractionDigits: 2 
                  })} USD
                </span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>Выведено прибыль:</strong>{' '}
                <span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>
                  {partner.totalWithdrawn.toLocaleString('ru-RU', { 
                    minimumFractionDigits: 0, 
                    maximumFractionDigits: 2 
                  })} USD
                </span>
              </div>
              <div>
                <strong>Баланс прибыль:</strong>{' '}
                <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                  {partner.balance.toLocaleString('ru-RU', { 
                    minimumFractionDigits: 0, 
                    maximumFractionDigits: 2 
                  })} USD
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Space style={{ marginBottom: 16 }}>
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          Добавить
        </Button>
        <Button type="default" icon={<PrinterOutlined />} onClick={handlePrint}>
          Печать
        </Button>
      </Space>
      <Table
        loading={loading}
        columns={columns}
        dataSource={investments}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />
      <Modal
        title="Добавить инвестицию"
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите Кассу">
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Select.Option key={cashbox._id} value={cashbox._id}>
                  {cashbox.name} | {cashbox.currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: "Пожалуйста, введите сумму" }]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[
              { required: true, message: "Пожалуйста, введите описание" },
            ]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
